import moment from "moment";
import { axios } from "../connection/ConnectionHandler";
import { createCrudActions } from '../utils/crudGenerator';
import { SHOW_NOTIFICATION, UPDATE_VISIT } from "../constants";

const weekdayMap = {
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
};

/**
 * Elimina las visitas que ya no coinciden con los días de la semana del cliente.
 * Si customerId se proporciona, solo se procesan visitas de ese cliente.
 */
export const syncVisitsToCustomerWeekdays = (customerId = null) => async (dispatch, getState) => {
	const state = getState();
	const visits = state.visits || {};
	const customers = state.customers || {};
	const visitsArray = Object.values(visits);

	const toDelete = [];
	for (const visit of visitsArray) {
		if (visit.manual) continue;
		if (!visit.date || !visit.customer) continue;
		const customer = customers[visit.customer];
		if (!customer) continue;
		if (customerId && customer._id !== customerId) continue;

		const visitWeekday = moment(visit.date).isoWeekday();
		const weekdayName = Object.keys(weekdayMap).find((k) => weekdayMap[k] === visitWeekday);
		const customerWeekdays = Array.isArray(customer.weekdays) ? customer.weekdays : [];

		if (!customerWeekdays.includes(weekdayName)) {
			toDelete.push(visit._id);
		}
	}

	if (toDelete.length > 0) {
		await dispatch(deleteVisits(toDelete));
	}
	return toDelete;
};

const formatVisits = (data) => {
    if (!data) {
        return [];
    }
    const formatItem = (item) => ({
        ...item,
        label: `${item.name}`,
        value: item._id,
        date: item.date && moment(item.date),
    });

    if (Array.isArray(data)) {
        return data.map(formatItem);
    }
    return formatItem(data);
};

export const {
    fetch: fetchVisits,
    create: createVisit,
    update: updateVisit,
    delete: deleteVisits
} = createCrudActions('VISIT', "Visita", formatVisits);

/** Reordena visitas por id; visitIds es el array ordenado. Actualiza el store y muestra una notificación. */
export const reorderVisits = (visitIds) => async (dispatch, getState) => {
    try {
        await axios.put("visit/reorder", { visitIds });
        await dispatch(fetchVisits());
        dispatch({
            type: "SHOW_NOTIFICATION",
            payload: { show: true, status: "success", message: "Orden de visitas actualizado." },
        });
    } catch (err) {
        console.error(err);
        dispatch({
            type: "SHOW_NOTIFICATION",
            payload: { show: true, status: "error", message: "Error al cambiar el orden." },
        });
    }
};

const orderSaveQueue = new Map();

const toOrderPayload = (raw, prepared) => {
    const item = typeof raw === "string" ? { reference: raw, quantity: 1, price: 0 } : raw || {};
    return {
        reference: item.reference || "",
        quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
        price: item.price != null ? Number(Number(item.price).toFixed(2)) : 0,
        prepared: prepared === true,
    };
};

/** Marca un producto del pedido como preparado (o no) y lo guarda sin aviso de éxito. */
export const setVisitOrderPrepared = (visitId, orderIndex, prepared) => (dispatch, getState) => {
    const run = async () => {
        const visit = getState().visits?.[visitId];
        if (!visit || !Array.isArray(visit.orders)) return;

        const previousOrders = visit.orders;
        const orders = previousOrders.map((raw, index) => {
            const item = typeof raw === "string" ? { prepared: false } : raw || {};
            const nextPrepared = index === orderIndex ? prepared : item.prepared === true;
            return toOrderPayload(raw, nextPrepared);
        });

        const optimistic = {
            ...visit,
            orders,
        };
        dispatch({ type: UPDATE_VISIT, payload: formatVisits(optimistic) });

        try {
            const body = { ...visit, orders };
            delete body.label;
            delete body.value;
            if (body.date) {
                body.date = moment(body.date).toISOString();
            }
            const response = await axios.put(`visit/${visitId}`, body);
            dispatch({ type: UPDATE_VISIT, payload: formatVisits(response.data) });
        } catch (err) {
            console.error(err);
            dispatch({
                type: UPDATE_VISIT,
                payload: formatVisits({ ...visit, orders: previousOrders }),
            });
            dispatch({
                type: SHOW_NOTIFICATION,
                payload: {
                    show: true,
                    status: "error",
                    message: "No se ha podido guardar el check list.",
                },
            });
        }
    };

    const previous = orderSaveQueue.get(visitId) || Promise.resolve();
    const next = previous.then(run, run);
    orderSaveQueue.set(visitId, next.catch(() => {}));
    return next;
};
